import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { getDefaultAgendaImage } from '../../../../../../store/utils/create-event';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import { LanguagesAbbr, PageModule, Session } from '../../../../../../types/working-model';
import { sessionsFromVodOrder } from '../../../../../../utils/utils';
import { Button } from '../../../../../general-ui/button/button';
import { EIcon } from '../../../../../general-ui/icon/icon';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../../../../live-event/utils';
import SessionCard from './agenda-session-card';
import { filterSessions, SessionFilter, SessionFilterArgs } from './session-filter';

interface OnDemandSessionsProps {
	module: PageModule;
	template: string;
	sessions: Session[];
	isLive: boolean;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
}
export const OnDemandSessions = ({
	module,
	template,
	sessions,
	isLive,
	language,
	baseLanguage
}: OnDemandSessionsProps): JSX.Element => {
	const [filter, setFilter] = useState<SessionFilterArgs>();
	const [showMore, setShowMore] = useState(false);

	const settings = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings);
	const maxFeaturedSessions = settings?.max_featured_sessions;

	const stylingOverrides = getStylingOverrides(module.styling_overrides);
	const { t } = useTranslate('homepage');

	const filteredSessions = useMemo<Session[]>(
		() => filterSessions({ sessions, filter }),
		[sessions, filter]
	);
	const sessionList = maxFeaturedSessions && !showMore
		? filteredSessions.slice(0, maxFeaturedSessions)
		: filteredSessions;

	const sortedSessions = useMemo(() => {
		const sorted = sessionsFromVodOrder<Session>(sessionList, settings?.vodOrder);
		return sorted;
	}, [settings?.vodOrder, sessionList]);

	return (
		<div
			className={classNames(
				'agenda-ondemand-sessions-container agenda-sessions-wrapper'
			)}
		>
			<div
				className={classNames(
					'agenda-ondemand-sessions-header agenda-sessions-header'
				)}
			>
				{!isLive &&
					<TypographyItem
						className={classNames('agenda-sessions-header-title evt-typography-h3')}
						tagName="h3"
					>
						{t('On demand sessions')}
					</TypographyItem>
				}

				<SessionFilter
					isLive={isLive}
					module={module}
					sessions={sessions}
					template={template}
					onFilterChange={setFilter}
					overrides={stylingOverrides.dropdownOverrides}
				/>
			</div>
			<div
				className={classNames('agenda-ondemand-sessions agenda-sessions-list')}
			>
				{sortedSessions.map((session: Session, i: number) => {
					return (
						<SessionCard
							baseLanguage={baseLanguage}
							language={language}
							key={session.uuid}
							session={session}
							stylingOverrides={stylingOverrides}
							template={template}
							defaultImage={getDefaultAgendaImage(template, i)}
						/>
					);
				})}
			</div>
			{!!maxFeaturedSessions && filteredSessions.length > maxFeaturedSessions && (
				<div className="agenda-show-more-wrapper">
					<Button
						onClick={() => setShowMore(!showMore)}
						classButton="secondary show-more-btn"
						template={template}
					>
						{template === TemplateClassNames.Apollo && (
							<EIcon
								name="refresh-transparent"
								position="order-left"
								size="medium"
							/>
						)}
						{t(showMore ? "Less" : "Show more")}
					</Button>
				</div>
			)}
		</div>
	);
};
